import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { create, useModal } from '@ebay/nice-modal-react';

interface CarDetailsDialogProps {
  car: any;
}

const CarDetailsDialog = create<CarDetailsDialogProps>(({ car }) => {
  if (!car) return null;
  const modal = useModal();

  return (
    <Dialog open={modal.visible} onClose={modal.hide} maxWidth="lg" fullWidth>
      <DialogTitle>
        {`${car.build.year} ${car.build.make} ${car.build.model}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            {/* Main Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Vehicle Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Price & Location
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    ${car.price?.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    {car.dealer.name} • {car.dealer.city}, {car.dealer.state}
                    {car.dist && ` • ${car.dist} miles away`}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Vehicle Specifications
                  </Typography>
                  <Grid container spacing={2}>
                    {car.build.trim && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Trim
                        </Typography>
                        <Typography variant="body1">
                          {car.build.trim}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Mileage
                      </Typography>
                      <Typography variant="body1">
                        {car.miles?.toLocaleString()} miles
                      </Typography>
                    </Grid>
                    {car.build.engine && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Engine
                        </Typography>
                        <Typography variant="body1">
                          {car.build.engine}
                        </Typography>
                      </Grid>
                    )}
                    {car.build.transmission && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Transmission
                        </Typography>
                        <Typography variant="body1">
                          {car.build.transmission}
                        </Typography>
                      </Grid>
                    )}
                    {car.build.body_type && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Body Style
                        </Typography>
                        <Typography variant="body1">
                          {car.build.body_type}
                        </Typography>
                      </Grid>
                    )}
                    {car.build.fuel_type && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Fuel Type
                        </Typography>
                        <Typography variant="body1">
                          {car.build.fuel_type}
                        </Typography>
                      </Grid>
                    )}
                    {car.exterior_color && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Exterior Color
                        </Typography>
                        <Typography variant="body1">
                          {car.exterior_color}
                        </Typography>
                      </Grid>
                    )}
                    {car.interior_color && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Interior Color
                        </Typography>
                        <Typography variant="body1">
                          {car.interior_color}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {car.features && car.features.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Features
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {car.features.map((feature: string, index: number) => (
                        <Chip key={index} label={feature} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {car.vdp_url && (
                <Button
                  variant="contained"
                  color="primary"
                  href={car.vdp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 3 }}
                  fullWidth
                >
                  View on Dealer Site
                </Button>
              )}
            </Grid>

            {/* Photo Gallery */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Photo Gallery
              </Typography>
              <Grid container spacing={2}>
                {car.media?.photo_links?.map((photo: string, index: number) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 1,
                      }}
                    >
                      <img
                        src={photo}
                        alt={`${car.build.year} ${car.build.make} ${
                          car.build.model
                        } - Photo ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

export default CarDetailsDialog;
